<script lang="ts">
import * as ace from "brace"
import "brace/mode/javascript"
import "brace/theme/solarized_dark"
import "brace/ext/emmet";
import { onMount, onDestroy } from "svelte"

export let value: string = ""; // String, required
export let lang: string = "json"; // String
export let theme: string = "chrome"; // String
export let options: any = {}; // Object
export let readonly: boolean = false;
export let allowBreakpoints: boolean = true;
export let selectionStartIndex:number = -1;
export let selectionEndIndex:number = -1;
export let selectionStartPoint:ace.Ace.Point = {row:-1, column:-1};
export let selectionEndPoint:ace.Ace.Point = {row:-1, column:-1};

let editorElement: HTMLElement;
let editor:ace.Ace.Editor;
let contentBackup:string = "";
let rowToIndices:Array<number> = new Array<number>();
let rowToIndicesIsValid:boolean = false;
let breakpoints:Array<string> = new Array<string>(); //TODO: Later export this if we save breakpoints with seesion and use saved breakpoints
let className:string = "";
export { className as class }

onDestroy(() => {
    if (editor) {
      editor.destroy();
      //editor.container.remove();
    }
});

$: watchValue(value);
function watchValue(val:string) {
  if (contentBackup !== val && editor && typeof val === "string") {
    editor.session.setValue(val);
    contentBackup = val;
    rowToIndicesIsValid = false;
  }
}

$: watchTheme(theme);
function watchTheme(newTheme: string) {
  if (editor) {
    editor.setTheme("ace/theme/" + newTheme);
  }
}

$: watchMode(lang);
function watchMode(newOption: any) {
  if (editor) {
    editor.getSession().setMode("ace/mode/" + newOption);
  }
}

$: watchOptions(options);
function watchOptions(newOption: any) {
  if (editor) {
    editor.setOptions(newOption);
  }
}

$: watchReadOnlyFlag(readonly);
function watchReadOnlyFlag(flag: boolean) {
  if (editor) {
    editor.setReadOnly(flag);
  }
}

$: watchSelectionIndex(selectionStartIndex, selectionEndIndex);
function watchSelectionIndex(startIndex:number, endIndex:number) {
  if (editor) {
    let startPoint:ace.Ace.Point = calculatePointFromIndex(startIndex);
    let endPoint:ace.Ace.Point = calculatePointFromIndex(endIndex);
    //console.log("indices: " + startIndex + ":" + endIndex);
    let range:ace.Ace.Range = ace.Range.fromPoints(startPoint, endPoint);
    //console.log("range: " + JSON.stringify(range));
    editor.selection.setRange(range);
  }
}

$: watchSelection(selectionStartPoint, selectionEndPoint);
function watchSelection(startPoint:ace.Ace.Point, endPoint:ace.Ace.Point) {
  if (editor) {
    let range:ace.Ace.Range = ace.Range.fromPoints(startPoint, endPoint);
    //console.log("range: " + JSON.stringify(range));
    editor.selection.setRange(range);
  }
}

onMount(
  () => { 
    editor = ace.edit(editorElement);
    // @ts-ignore: Property does not exist on type
    editor.$blockScrolling = Infinity;
    editor.getSession().setMode("ace/mode/" + lang);
    editor.setTheme("ace/theme/" + theme);
    editor.setValue(value, 1);
    editor.setReadOnly(readonly)
    editor.clearSelection();
    contentBackup = value;
    setEventCallBacks();
    if (options) {
      editor.setOptions(options);
    }

    editor.commands.removeCommand("indent");
    editor.commands.removeCommand("outdent");
  }
)

function setEventCallBacks() {
  editor.on("change", function () {
    const content = editor.getValue();
    value = content;
    //dispatch("input", content);
    contentBackup = content;
    rowToIndicesIsValid = false;
  });

  editor.on("guttermousedown", function(e) {
    if (!allowBreakpoints) { //Breakpoints are not allowed!
      return;
    }

    var target = e.domEvent.target;

    if (target.className.indexOf("ace_gutter-cell") == -1){
        return;
    }

    /*if (!editor.isFocused()){
        return; 
    }*/

    //Exclude the part after the line number
    /*if (e.clientX > 25 + target.getBoundingClientRect().left){
        return;
    }*/

    var row = e.getDocumentPosition().row;

    // If there's a breakpoint already defined, it should be removed, offering the toggle feature
    if(typeof breakpoints[row] === typeof undefined){
        e.editor.session.setBreakpoint(row);
    }else{
        e.editor.session.clearBreakpoint(row);
    }

    breakpoints = e.editor.session.getBreakpoints(row, 0);

    e.stop();
  });
}

function calculatePointFromIndex(index:number):ace.Ace.Point {
  calculateLineLengths();
  //console.log("looking for index: " + index);

  let row:number = -1;
  let column:number = -1;

  let startRow = 0, endRow = rowToIndices.length - 1;
  //console.log("start: " + startRow + " end: " + endRow);
         
  // Iterate while start not meets end
  while (startRow <= endRow){
    // Find the mid index
    let middleRow = Math.floor((startRow + endRow) / 2);
    let middleIndex = rowToIndices[middleRow];
    
    if (index < middleIndex) {
      endRow = middleRow - 1;
      //console.log("start: " + startRow + " end: " + endRow);
    }
    else if (index >= rowToIndices[middleRow + 1]) {      
      startRow = middleRow + 1;
      //console.log("start: " + startRow + " end: " + endRow);
    }
    else {
      row = middleRow;
      column = index - middleIndex;
      //console.log("row: " + row + " column: " + column);
      break;
    }
  }
  //console.log("index: " + index + " (" + row + "," + column + ")");
  return {row, column};
}

function calculateLineLengths() {
  if (!rowToIndicesIsValid) {
    rowToIndices.length = 0;
    let runningIndex:number = 0;
    const document:ace.Ace.Document = editor.session.getDocument();
    const newLineLength = document.getNewLineCharacter().length;
    for (let row:number = 0; row < document.getLength(); row++) {
      let line:string = document.getLine(row);
      rowToIndices.push(runningIndex);
      runningIndex += (line.length + newLineLength); 
    }
    rowToIndices.push(runningIndex);
    //console.log("rowToIndices: " + JSON.stringify(rowToIndices));
    rowToIndicesIsValid = true;
  }
}

export function getLineNumber(characterIndex:number):number {
  //Make sure rowToIndices is valid.
  calculateLineLengths();
  //Find out what line it's on using binary search over rowToIndices.
  let lineNumber:number = binarySearch(rowToIndices, 
    (value:number) => characterIndex <= value
  );
  //Convert binarySearch value from length to -1 if needed.
  return lineNumber != rowToIndices.length ? lineNumber : -1;
}

export function hasBreakpoint(lineNumber:number):boolean {
  //See if line it's on is in breakpoints: see if we found a value and if there's a break point there.
  return !(typeof breakpoints[lineNumber] === typeof undefined);
}

/**
  * Return 0 <= i <= array.length such that !pred(array[i - 1]) && pred(array[i]).
  * array.length means it was not found.
  */
function binarySearch(array:Array<number>, pred:(value:number) => boolean) {
  let lo = -1, hi = array.length;
  while (1 + lo < hi) {
      const mi = lo + ((hi - lo) >> 1);
      if (pred(array[mi])) {
          hi = mi;
      } else {
          lo = mi;
      }
  }
  return hi;
}

</script>

<div bind:this={editorElement} class={className}></div>
